import type { ReactNode } from 'react';
import { ApolloProvider, type ApolloClient, type NormalizedCacheObject } from '@apollo/client';
import { ThemeProvider } from '@/context/ThemeContext';
import { AuthProvider } from '@/context/AuthContext';
import { NotificationProvider } from '@/context/NotificationContext';

interface AppProvidersProps {
  /**
   * The browser's client (`getApolloClient()`); tests and the server-render
   * harness pass their own — the server never creates one (web plan W4.2).
   */
  client: ApolloClient<NormalizedCacheObject>;
  children: ReactNode;
}

/**
 * Every context the app needs, in order: theme, session, Apollo,
 * notifications. The document's root route (src/root.tsx, W4.3) wraps every
 * page in it inside the router (main.tsx did before), and so do the
 * server-render tests, so both render the same tree.
 */
export function AppProviders({ client, children }: AppProvidersProps) {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ApolloProvider client={client}>
          <NotificationProvider>
            {/* No GoogleOAuthProvider here: it injects Google's GSI script
                wherever it mounts, so it lives in GoogleIdentityScope
                around the only UI that signs in with Google. */}
            {children}
          </NotificationProvider>
        </ApolloProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default AppProviders;
